/**
 * System Announcement Store
 * Manages system-wide announcements displayed to users on login.
 */
import { create } from 'zustand'
import { api } from '@/lib/api'

// LocalStorage key to track if user has dismissed the current announcement
const DISMISSED_KEY = 'kcs-announcement-dismissed'

interface SystemAnnouncement {
    is_enabled: boolean
    message: string
    updated_at?: string
    updated_by_email?: string
}

interface AnnouncementState {
    announcement: SystemAnnouncement | null
    isLoading: boolean
    error: string | null
    showModal: boolean

    // Actions
    fetchAnnouncement: () => Promise<void>
    updateAnnouncement: (data: Partial<SystemAnnouncement>) => Promise<void>
    dismissModal: () => void
    checkAndShowModal: () => void
}

export const useAnnouncementStore = create<AnnouncementState>((set, get) => ({
    announcement: null,
    isLoading: false,
    error: null,
    showModal: false,

    fetchAnnouncement: async () => {
        set({ isLoading: true, error: null })
        try {
            const data = await api.get<SystemAnnouncement>('/maintenance/announcement/')
            set({ announcement: data, isLoading: false })
        } catch (err) {
            const error = err as { status?: number; message?: string }
            set({
                error: error.message || 'Failed to fetch announcement',
                isLoading: false
            })
        }
    },

    updateAnnouncement: async (data: Partial<SystemAnnouncement>) => {
        set({ isLoading: true, error: null })
        try {
            const result = await api.put<SystemAnnouncement>('/maintenance/announcement/', data)
            set({ announcement: result, isLoading: false })
            // Clear dismissed state when announcement is updated
            localStorage.removeItem(DISMISSED_KEY)
        } catch (err) {
            const error = err as { status?: number; message?: string }
            set({
                error: error.message || 'Failed to update announcement',
                isLoading: false
            })
            throw err
        }
    },

    dismissModal: () => {
        const { announcement } = get()
        if (announcement?.updated_at) {
            // Store the timestamp of the dismissed announcement
            localStorage.setItem(DISMISSED_KEY, announcement.updated_at)
        }
        set({ showModal: false })
    },

    checkAndShowModal: () => {
        const { announcement } = get()
        if (!announcement?.is_enabled) {
            set({ showModal: false })
            return
        }

        // Check if user has dismissed this version of the announcement
        const dismissedAt = localStorage.getItem(DISMISSED_KEY)
        if (dismissedAt && announcement.updated_at === dismissedAt) {
            // User has already seen and dismissed this announcement
            set({ showModal: false })
            return
        }

        // Show the modal
        set({ showModal: true })
    },
}))
